import { useMemo } from 'react';
import { setIn, ValidationErrors } from 'final-form';
import { AnySchema, ValidationError } from 'yup';

/**
 * Sets the `innerError.message` in an `errors` object at the key
 * defined by `innerError.path`.
 */
const setInError = (errors: Record<string, string>, innerError: ValidationError) => {
  return setIn(errors, innerError.path ?? '', innerError.message);
};

/**
 * Empty object map with no prototype. Used as default
 * value for reducing the `err.inner` array of errors
 * from a `yup~ValidationError`.
 */
const emptyObj: {} = Object.create(null);

/**
 * Takes a `yup` validation schema and returns a function that expects
 * a map of values to validate. If the validation passes, the function returns `undefined`
 * (signalling that the values are valid). If the validation doesn't pass, it returns
 * a map of invalid field names to errors.
 *
 * NB: Schemas can only use synchronous validation - if there's async validation this will error
 */

export function makeValidate<FormValues = object>(schema: AnySchema): (val: FormValues) => ValidationErrors {
  return function validate(values: FormValues) {
    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (err) {
      return (err as ValidationError).inner.reduce(setInError, emptyObj);
    }
  };
}

export function useValidationSchema(schema: AnySchema) {
  const validate = useMemo(() => makeValidate(schema), [schema]);
  return validate;
}
